import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText } from "lucide-react";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const QuoteRequestDialog = ({ product }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    company: user?.company || "",
    quantity: 10,
    message: "",
    website: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/quote-requests", {
        ...form,
        quantity: Number(form.quantity),
        product_id: product.id,
        product_name: `${product.name} (${product.strength})`,
      });
      toast.success(data.message);
      setOpen(false);
      setForm((f) => ({ ...f, quantity: 10, message: "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" className="w-full rounded-full border-slate-300" data-testid="request-quote-btn">
          <FileText className="h-4 w-4 mr-2" /> Request Bulk Quote
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="quote-request-dialog">
        <DialogHeader>
          <DialogTitle>Request a Bulk Quote</DialogTitle>
          <DialogDescription>
            Institutional pricing for {product.name}. We'll respond within one business day.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email-input" />
            </div>
          </div>
          <div>
            <Label>Company / Institution (optional)</Label>
            <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Laboratory, university, or research organization" data-testid="quote-company-input" />
          </div>
          <div>
            <Label>Quantity Needed</Label>
            <Input type="number" min={1} required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="quote-quantity-input" />
          </div>
          <div>
            <Label>Message (optional)</Label>
            <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Timeline, recurring needs, documentation requirements..." data-testid="quote-message-input" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full rounded-full btn-lift" data-testid="quote-submit-btn">
            {submitting ? "Submitting..." : "Submit Quote Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
